// Copyright © 2025 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { push } from 'connected-react-router';
import styles from './styles';
import {
  FormActionButtons,
  TabPanel,
  TabsContainer,
} from '../../components';
import { getI18n, getI18nFormattedMessage } from '../../utils/intl';

import DetailsContainer from '../details';
import ExpiryReportingSettings from './ExpiryReporting';
import ApplicationSecretSettings from './SecretSettings';
import AuthProviders from './AuthProviders';
import { Typography, makeStyles, withStyles } from "@material-ui/core";

import {
  executeExpiryJob,
  executeExpiryReportingJob,
  fetchAPIKeySettings,
  updateAPIKeySettings,
} from '../../actions/apiKeySettings';
import {
  getIsError,
  getErrors,
  getErrorFields,
  getExpiryJobStatus,
  getIsExecutingExpiryJob,
  getReportingJobStatus,
  getIsExecutingReportingJob,
  getIsLoading,
  getAPIKeySettings,
  getUpdateKeySettingsStatus,
} from '../../reducers/apiKeySettings';
import { getUserDetails } from '../../reducers/portalConfig';

import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  FAIL,
  SUCCESS,
} from "../../constants";
import { findIndex, get } from 'lodash';

const getTabItems = () => [
  {
    id: 'clientid-secret',
    tabId: 'application-secret-settings',
    label: getI18nFormattedMessage('label.settings.application.keysettings.secret'),
    visible: true,
  },
  {
    id: 'auth-providers',
    tabId: 'application-auth-providers-settings',
    label: getI18nFormattedMessage('label.settings.application.keysettings.auth.providers'),
    visible: true,
  },
  {
    id: 'expiry-reporting',
    tabId: 'application-expiry-reporting-settings',
    label: getI18nFormattedMessage('label.settings.application.keysettings.expiry.reporting'),
    visible: true,
  },
];



const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);

const valuesToPayload = (valuesObj = { }) => {
  const {
    allowPlainSecrets,
    allowHashedSecrets,
    algorithm,
    expireKeys,
    notificationsEnabled,
    daysUntilExpiry,
    reportCadence,
    reportDay,
    sendReportToOrgPublisher,
    sendReportToAdmin,
    startReporting,
    urgentReporting,
    reportIncludes,
  } = valuesObj;
  const hashSettings = {
    plaintextAllowed: !!allowPlainSecrets,
    ...(allowHashedSecrets ? { algorithm } : {}),
  };
  let expirySettings = {
    enabled: expireKeys,
    expiryDays: daysUntilExpiry,
    reportAsUrgentBeforeDays: urgentReporting,
    startReportingBeforeDays: startReporting,
    notificationsEnabled,
  };
  if (notificationsEnabled) {
    expirySettings = {
      ...expirySettings,
      notifyOrgPublishersIfNoOrgAdmins: !!sendReportToOrgPublisher,
      notifyAdminsIfNoOrgAdmins: !!sendReportToAdmin,
      reportCadence,
      reportDay,
      reportIncludes,
    };
  }

  return {
    appSecretHashingMetadata: hashSettings,
    applicationApiKeyExpiry: expirySettings,
  };
};
const areValuesDifferent = (valObj1, valObj2) => {
  const {
    allowPlainSecrets: allowPlainSecrets1,
    allowHashedSecrets: allowHashedSecrets1,
    algorithm: algorithm1,
    notificationsEnabled: notificationsEnabled1,
    expireKeys: expireKeys1,
    daysUntilExpiry: daysUntilExpiry1,
    reportCadence: reportCadence1,
    reportDay: reportDay1,
    sendReportToOrgPublisher: sendReportToOrgPublisher1,
    sendReportToAdmin: sendReportToAdmin1,
    startReporting: startReporting1,
    urgentReporting : urgentReporting1,
    reportIncludes: reportIncludes1,
  } = valObj1;
  const {
    allowPlainSecrets: allowPlainSecrets2,
    allowHashedSecrets: allowHashedSecrets2,
    algorithm: algorithm2,
    notificationsEnabled: notificationsEnabled2,
    expireKeys: expireKeys2,
    daysUntilExpiry: daysUntilExpiry2,
    reportCadence: reportCadence2,
    reportDay: reportDay2,
    sendReportToOrgPublisher: sendReportToOrgPublisher2,
    sendReportToAdmin: sendReportToAdmin2,
    startReporting: startReporting2,
    urgentReporting : urgentReporting2,
    reportIncludes: reportIncludes2,
  } = valObj2;

  if (
    (allowPlainSecrets1 !== allowPlainSecrets2) ||
    (allowHashedSecrets1 !== allowHashedSecrets2) ||
    ((allowHashedSecrets1 === allowHashedSecrets2) &&
    allowHashedSecrets2 && (algorithm1 !== algorithm2))
  ) {
    return true;
  }

  if (expireKeys1 !== expireKeys2) {
    return true;
  }

  if (
    // eslint-disable-next-line eqeqeq
    daysUntilExpiry1 != daysUntilExpiry2
  ) {
    return true;
  }

  if (notificationsEnabled1 !== notificationsEnabled2) {
    return true;
  }

  if (
    (reportCadence1 && reportCadence2 && (reportCadence1 !== reportCadence2)) ||
    (reportDay1 && reportDay2 && (reportDay1 !== reportDay2)) ||
    (sendReportToOrgPublisher1 !== sendReportToOrgPublisher2) ||
    (sendReportToAdmin1 !== sendReportToAdmin2) ||
    (startReporting1 !== startReporting2) ||
    (urgentReporting1 !== urgentReporting2)
  ) {
    return true;
  }
  if(get(reportIncludes1, 'secret') !== get(reportIncludes2, 'secret') ||
    get(reportIncludes1, 'certificate') !== get(reportIncludes2, 'certificate')) {
    return true;
  }
  return false;
};

const getDefaultSelectedTabIndex = (tabs, match) => {
  const tabName = get(match, 'params.tabName');
  const index = findIndex(tabs, tab => (tab.id === tabName));
  // if none found default to 0
  return (index > 0 ? index : 0);
};

export const ApplicationKeySettings = (props) => {
  const {
    apiKeySettings,
    errorFields,
    errors,
    expiryJobStatus,
    reportingJobStatus,
    fetchAPIKeySettings,
    history,
    isExecutingExpiryJob,
    isExecutingReportingJob,
    isLoading,
    match,
    updateAPIKeySettings,
    updateStatus,
    userContext = {},
  } = props;
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const intl = getI18n(useIntl());


  const defaultValues = {
    allowPlainSecrets: false,
    allowHashedSecrets: false,
    algorithm: 'SHA-512',
    expireKeys: false,
    daysUntilExpiry: 120,
    applyToKeysWithoutExpiry: false,
    applyToKeysWithLongerExpiry: false,
    notificationsEnabled: false,
    reportCadence: 'MONTHLY',
    reportDay: 'FIRST',
    sendReportToOrgPublisher: false,
    sendReportToAdmin: false,
    startReporting: 90,
    urgentReporting: 60,
  };

  const tabItems = getTabItems();
  const tabs = getTabs(tabItems);
  const defaultIndex = getDefaultSelectedTabIndex(tabs, match);
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(tabs[defaultIndex].tabId);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');

  const [formValues, setFormValues] = useState(defaultValues);
  const [startReportingError, setStartReportingError] = useState(false);
  const [urgentReportingError, setUrgentReportingError] = useState(false);
  const [expiryReportingError, setExpiryReportingError] = useState(false);

const payloadToValues = (payload = {}) => {
  const {
    applicationApiKeyExpiry: {
      enabled,
      expiryDays,
      notificationsEnabled,
      reportCadence,
      reportDay,
      notifyOrgPublishersIfNoOrgAdmins,
      notifyAdminsIfNoOrgAdmins,
      reportAsUrgentBeforeDays,
      startReportingBeforeDays,
      reportIncludes,
    } = { },
    appSecretHashingMetadata: {
      algorithm,
      plaintextAllowed,
    } = { },
  } = payload;

  return {
    allowPlainSecrets: !!plaintextAllowed,
    allowHashedSecrets: !!algorithm,
    ...(algorithm ? { algorithm } : {}),
    expireKeys: enabled,
    daysUntilExpiry: expiryDays,
    notificationsEnabled,
    ...(reportCadence ? { reportCadence } : {}),
    ...(reportDay ? { reportDay } : {}),
    sendReportToOrgPublisher: !!notifyOrgPublishersIfNoOrgAdmins,
    sendReportToAdmin: !!notifyAdminsIfNoOrgAdmins,
    startReporting: startReportingBeforeDays,
    urgentReporting: reportAsUrgentBeforeDays,
    ...(reportIncludes ? { reportIncludes } : {}),
  };
};
const boolFields = [
  'allowPlainSecrets',
  'allowHashedSecrets',
  'expireKeys',
  'applyToKeysWithoutExpiry',
  'applyToKeysWithLongerExpiry',
  'notificationsEnabled',
  'sendReportToOrgPublisher',
  'sendReportToAdmin',
];
const handleInputChange = (e) => {
  const { name, value, checked } = e.target;
  if (boolFields.includes(name)) {
    if (name === 'expireKeys') {
      setExpiryReportingError(false);
      setUrgentReportingError(false);
      setStartReportingError(false);
      if (checked) {
        setFormValues({
          ...formValues,
          expireKeys: true,
          daysUntilExpiry: formValues.daysUntilExpiry || 120,
          startReporting: formValues.startReporting || 90,
          urgentReporting: formValues.urgentReporting || 60,
        });
      } else {
        const isCertReportIncluded = get(formValues, 'notificationsEnabled')
                                    && get(formValues, 'reportIncludes.certificate');
        setFormValues({
          ...formValues,
          expireKeys: false,
          notificationsEnabled: isCertReportIncluded,
          applyToKeysWithoutExpiry: false,
          applyToKeysWithLongerExpiry: false,
          reportIncludes: {
            ...formValues.reportIncludes,
            secret: false,
          },
        });
      }
    } else {
      setFormValues({
        ...formValues,
        [name]: checked,
      });
    }
  } else {
    if(name === 'daysUntilExpiry' && parseInt(get(apiKeySettings, 'applicationApiKeyExpiry.expiryDays')) === parseInt(value)) {
      setFormValues({
        ...formValues,
        [name]: value,
        applyToKeysWithoutExpiry: false,
        applyToKeysWithLongerExpiry: false,
      });
    } else {
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }
  }
};

  const initialValues = payloadToValues(apiKeySettings);
  const isFormDirty = areValuesDifferent(initialValues, formValues);
  useEffect(() => {
    fetchAPIKeySettings();
  }, []);

  useEffect(() => {
    if (!userContext.portalAdmin) {
      props.push('/404');
    }
  }, [userContext]);

  useEffect(() => {
    if (expiryJobStatus === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.settings.application.keysettings.expire.job.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
    } else if (expiryJobStatus === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.settings.application.keysettings.expire.job.error'))
    }
  }, [expiryJobStatus]);

  useEffect(() => {
    if (reportingJobStatus === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.settings.application.keysettings.reporting.job.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
    } else if (reportingJobStatus === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.settings.application.keysettings.reporting.job.error'))
    }
  }, [reportingJobStatus]);

  useEffect(() => {
    if (updateStatus === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.settings.application.keysettings.update.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
      fetchAPIKeySettings();
    } else if (updateStatus === FAIL) {
      notifyMessages(ALERT_ERROR, errors || intl.getI18nMessage('label.settings.application.keysettings.update.error'));
      if (errorFields.length > 0) {
        const highLightError = errorFields[0];
        if (highLightError === 'startReportingBeforeDays') {
          setStartReportingError(true);
        } else if (highLightError === 'reportAsUrgentBeforeDays') {
          setUrgentReportingError(true);
        } else if (highLightError === 'expiryDays') {
          setExpiryReportingError(true);
        }
      }
    }
  }, [errorFields, updateStatus]);

  useEffect(() => {
    setFormValues({
      ...defaultValues,
      ...payloadToValues(apiKeySettings),
    });
  }, [apiKeySettings]);
  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const handleTabChange = (e, newValue) => {
    const newTabId = tabs[newValue].tabId;
    const newId = tabs[newValue].id;
    history.push('/admin/settings/application-settings' + '/' + newId);
    setTabIndex(newValue);
    setCurrentTab(newTabId);
  };
  const onCancel = () => {
    fetchAPIKeySettings();
  };
  const onSave = () => {
    setStartReportingError(false);
    setUrgentReportingError(false);
    setExpiryReportingError(false);
    updateAPIKeySettings(
      valuesToPayload(formValues),
      get(formValues,'applyToKeysWithoutExpiry'),
      get(formValues,'applyToKeysWithLongerExpiry'),
    );
  };

  return (
    <DetailsContainer
      detailsPageId="api-details-page"
      isLoading={false}
      notificationId="api-details-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationMessage}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      header={(<div className={classes.header}>
                <Typography variant="h1" className={classes.pageTitle}>
                  {intl.getI18nMessage("label.settings.application.keysettings")}
                </Typography>
              </div>
              )}
      footer=
        {(isFormDirty && <FormActionButtons
          id={"appBar"}
          isContainerFooter
          cancelText={intl.getI18nMessage("label.cancel.button")}
          nextText={intl.getI18nMessage("label.save.button")}
          onCancelClick={onCancel}
          onNextClick={onSave}
          isDisabled={!isFormDirty}
        />)}
      tabs={
        <TabsContainer
          id={'api-details-tab-container'}
          tabValue={tabIndex}
          orientation="horizontal"
          tabItems={tabs}
          handleTabChange={handleTabChange}
        />
      }
      page={
        <Fragment>
          <TabPanel
            visible={currentTab === 'application-secret-settings'}
            id="clientid-secret"
          >
            <ApplicationSecretSettings
              classes={classes}
              executeExpiryJob={props.executeExpiryJob}
              isExecutingExpiryJob={isExecutingExpiryJob}
              isExecutingReportingJob={isExecutingReportingJob}
              isLoading={isLoading}
              expiryReportingError={expiryReportingError}
              apiKeySettings={apiKeySettings}
              formValues={formValues}
              isFormDirty={isFormDirty}
              handleInputChange={handleInputChange}
              enablingExpiry={!get(apiKeySettings, 'applicationApiKeyExpiry.enabled')}
            />
          </TabPanel>
          <TabPanel
            visible={currentTab === 'application-auth-providers-settings'}
            id="auth-providers"
          >
            <AuthProviders
              classes={classes}
              history={history}
            />
          </TabPanel>
          <TabPanel
            visible={currentTab === 'application-expiry-reporting-settings'}
            id="expiry-reporting"
          >
            <ExpiryReportingSettings
              classes={classes}
              executeExpiryReportingJob={props.executeExpiryReportingJob}
              isExecutingExpiryJob={isExecutingExpiryJob}
              isExecutingReportingJob={isExecutingReportingJob}
              isLoading={isLoading}
              urgentReportingError={urgentReportingError}
              startReportingError={startReportingError}
              formValues={formValues}
              setFormValues={setFormValues}
              isFormDirty={isFormDirty}
              handleInputChange={handleInputChange}
            />
          </TabPanel>
        </Fragment>
      }
    />
  );
};

const mapStateToProps = state => ({
  isError: getIsError(state),
  expiryJobStatus: getExpiryJobStatus(state),
  reportingJobStatus: getReportingJobStatus(state),
  errorFields: getErrorFields(state),
  errors: getErrors(state),
  isExecutingExpiryJob: getIsExecutingExpiryJob(state),
  isExecutingReportingJob: getIsExecutingReportingJob(state),
  isLoading: getIsLoading(state),
  apiKeySettings: getAPIKeySettings(state),
  updateStatus: getUpdateKeySettingsStatus(state),
  userContext: getUserDetails(state),
});

const mapDispatchToProps = {
  executeExpiryJob,
  executeExpiryReportingJob,
  fetchAPIKeySettings,
  updateAPIKeySettings,
  push,
};

ApplicationKeySettings.propTypes = {
  classes: PropTypes.object,
  isError: PropTypes.bool,
  errorFields: PropTypes.array,
  errors: PropTypes.array,
  expiryJobStatus: PropTypes.string,
  isExecutingExpiryJob: PropTypes.bool,
  reportingJobStatus: PropTypes.string,
  isExecutingReportingJob: PropTypes.bool,
  isLoading: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      tabId: PropTypes.string,
    }),
  }),
  apiKeySettings: PropTypes.object,
  history: PropTypes.object,
  updateStatus: PropTypes.string,
  executeExpiryJob: PropTypes.func,
  executeExpiryReportingJob: PropTypes.func,
  fetchAPIKeySettings: PropTypes.func,
  updateAPIKeySettings: PropTypes.func,
  userContext: PropTypes.object,
  push: PropTypes.func,
};

ApplicationKeySettings.displayName = 'ApplicationKeySettings';

export default (
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(ApplicationKeySettings);
